import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { ZOOM_DISTANCE } from '../data/scenarios';
import { useScenario } from '../store/scenarioStore';

/**
 * Composant interne au Canvas. Quand un scénario est appliqué, anime la caméra
 * vers une position adaptée au zoom demandé. À placer comme enfant de <Canvas>.
 */
export default function CameraDirector() {
  const { camera, controls } = useThree() as { camera: THREE.PerspectiveCamera; controls: any };
  const pendingZoom = useScenario((s) => s.pendingZoom);
  const clearPending = useScenario((s) => s.clearPending);

  // Position cible (en unités scène) — re-calculée à chaque scénario
  const target = useRef<{ pos: THREE.Vector3; tgt: THREE.Vector3 } | null>(null);

  useEffect(() => {
    if (!pendingZoom) return;
    const dist = ZOOM_DISTANCE[pendingZoom];
    // Vue depuis "presque pôle nord écliptique", légèrement inclinée pour profondeur
    const newPos = new THREE.Vector3(0, dist * 0.55, dist);
    target.current = {
      pos: newPos,
      tgt: new THREE.Vector3(0, 0, 0),
    };
    // On laisse useFrame animer ; clearPending sera appelé en fin d'animation
  }, [pendingZoom]);

  useFrame(() => {
    if (!target.current) return;
    const { pos, tgt } = target.current;
    // Lerp linéaire vers la cible : alpha=0.08 = 1 cycle ≈ 30 frames ≈ 0.5s à 60fps
    camera.position.lerp(pos, 0.08);

    // OrbitControls expose un .target Vector3 pour le centre de rotation
    if (controls && controls.target instanceof THREE.Vector3) {
      controls.target.lerp(tgt, 0.08);
      controls.update?.();
    }

    // Critère de fin d'animation
    if (camera.position.distanceTo(pos) < 0.5) {
      camera.position.copy(pos);
      target.current = null;
      clearPending();
    }
  });

  return null;
}
